import { ElementType, Suspense, lazy } from 'react';
import { Navigate, useLocation, useRoutes } from 'react-router-dom';
// layouts
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
import MainLayout from '../layouts/MainLayout';
import PedigreeLayout from '../layouts/PedigreeLayout';

// Direct imports for pages
import NotFound from '../pages/Page404';
import LoadingScreen from '../components/LoadingScreen';

const Loadable = (Component: ElementType) => (props: any) => {
  const { pathname } = useLocation();

  return (
    <Suspense fallback={<LoadingScreen isDashboard={pathname.includes('/dashboard')} />}>
      <Component {...props} />
    </Suspense>
  );
};

// Lazy loaded modules for code splitting
// Member module
const MemberSearchPage = Loadable(
  lazy(() => import('../pages/member').then((module) => ({ default: module.MemberSearchPage })))
);
const MemberProfilePage = Loadable(
  lazy(() => import('../pages/member').then((module) => ({ default: module.MemberProfilePage })))
);
const AdminMemberManagementPage = Loadable(
  lazy(() =>
    import('../pages/member').then((module) => ({ default: module.AdminMemberManagementPage }))
  )
);

// Pedigree module
const PedigreeSearchPanel = Loadable(
  lazy(() =>
    import('../pages/pedigree').then((module) => ({ default: module.PedigreeSearchPanel }))
  )
);

// Herd module
const OwnedHerdListPage = Loadable(
  lazy(() => import('../pages/herd').then((module) => ({ default: module.OwnedHerdListPage })))
);
const HerdManagementPage = Loadable(
  lazy(() => import('../pages/herd').then((module) => ({ default: module.HerdManagementPage })))
);
const HerdAuditPage = Loadable(
  lazy(() => import('../pages/herd').then((module) => ({ default: module.HerdAuditPage })))
);
const HerdAuditEntry = Loadable(
  lazy(() => import('../pages/herd').then((module) => ({ default: module.HerdAuditEntry })))
);
const HerdAuditAdminYearListPage = Loadable(
  lazy(() =>
    import('../pages/herd').then((module) => ({ default: module.HerdAuditAdminYearListPage }))
  )
);
const HerdAuditAdminHerdsPage = Loadable(
  lazy(() =>
    import('../pages/herd').then((module) => ({ default: module.HerdAuditAdminHerdsPage }))
  )
);
const HerdAuditAdminEntryPage = Loadable(
  lazy(() =>
    import('../pages/herd').then((module) => ({ default: module.HerdAuditAdminEntryPage }))
  )
);

// Dam module
const AdlHerdPage = Loadable(
  lazy(() => import('../pages/dam').then((module) => ({ default: module.AdlHerdPage })))
);
const AdlFormEntryPage = Loadable(
  lazy(() => import('../pages/dam').then((module) => ({ default: module.AdlFormEntryPage })))
);
const AdlAdminYearListPage = Loadable(
  lazy(() => import('../pages/dam').then((module) => ({ default: module.AdlAdminYearListPage })))
);
const AdlAdminHerdsPanel = Loadable(
  lazy(() => import('../pages/dam').then((module) => ({ default: module.AdlAdminHerdsPanel })))
);
const AdlAdminFormEntryPage = Loadable(
  lazy(() => import('../pages/dam').then((module) => ({ default: module.AdlAdminFormEntryPage })))
);

// Calf module
const CalfReturnListPanel = Loadable(
  lazy(() => import('../pages/calf').then((module) => ({ default: module.CalfReturnListPanel })))
);
const CalfReturnEntryFormPanel = Loadable(
  lazy(() =>
    import('../pages/calf').then((module) => ({ default: module.CalfReturnEntryFormPanel }))
  )
);
const CalfReturnAdminYearListPage = Loadable(
  lazy(() =>
    import('../pages/calf').then((module) => ({ default: module.CalfReturnAdminYearListPage }))
  )
);
const CalfReturnAdminHerdsPage = Loadable(
  lazy(() =>
    import('../pages/calf').then((module) => ({ default: module.CalfReturnAdminHerdsPage }))
  )
);
const CalfReturnAdminEntryFormPage = Loadable(
  lazy(() =>
    import('../pages/calf').then((module) => ({ default: module.CalfReturnAdminEntryFormPage }))
  )
);

// Transfer module
const PedigreeTransfersPage = Loadable(
  lazy(() =>
    import('../pages/transfer').then((module) => ({ default: module.PedigreeTransfersPage }))
  )
);
const PedigreeTransferFormPage = Loadable(
  lazy(() =>
    import('../pages/transfer').then((module) => ({ default: module.PedigreeTransferFormPage }))
  )
);
const PedigreeTransferHistoryPage = Loadable(
  lazy(() =>
    import('../pages/transfer').then((module) => ({ default: module.PedigreeTransferHistoryPage }))
  )
);
const AdminPedigreeTransfersPage = Loadable(
  lazy(() =>
    import('../pages/transfer').then((module) => ({ default: module.AdminPedigreeTransfersPage }))
  )
);
const AdminPedigreeTransferFormPage = Loadable(
  lazy(() =>
    import('../pages/transfer').then((module) => ({
      default: module.AdminPedigreeTransferFormPage,
    }))
  )
);
const AdminPedigreeTransferHistoryPage = Loadable(
  lazy(() =>
    import('../pages/transfer').then((module) => ({
      default: module.AdminPedigreeTransferHistoryPage,
    }))
  )
);

// Registration module
const PedigreeRegistrationsPage = Loadable(
  lazy(() =>
    import('../pages/registration').then((module) => ({
      default: module.PedigreeRegistrationsPage,
    }))
  )
);
const PedigreeRegistrationFormPage = Loadable(
  lazy(() =>
    import('../pages/registration').then((module) => ({
      default: module.PedigreeRegistrationFormPage,
    }))
  )
);
const PedigreeRegistrationHistoryPage = Loadable(
  lazy(() =>
    import('../pages/registration').then((module) => ({
      default: module.PedigreeRegistrationHistoryPage,
    }))
  )
);
const AdminPedigreeRegistrationsPage = Loadable(
  lazy(() =>
    import('../pages/registration').then((module) => ({
      default: module.AdminPedigreeRegistrationsPage,
    }))
  )
);
const AdminPedigreeRegistrationFormPage = Loadable(
  lazy(() =>
    import('../pages/registration').then((module) => ({
      default: module.AdminPedigreeRegistrationFormPage,
    }))
  )
);
const AdminPedigreeRegistrationHistoryPage = Loadable(
  lazy(() =>
    import('../pages/registration').then((module) => ({
      default: module.AdminPedigreeRegistrationHistoryPage,
    }))
  )
);

// Authorization module
const AdminRolesPage = Loadable(
  lazy(() =>
    import('../pages/authorization').then((module) => ({ default: module.AdminRolesPage }))
  )
);
const AdminRoleFormPage = Loadable(
  lazy(() =>
    import('../pages/authorization').then((module) => ({ default: module.AdminRoleFormPage }))
  )
);
const AdminOfficesPage = Loadable(
  lazy(() =>
    import('../pages/authorization').then((module) => ({ default: module.AdminOfficesPage }))
  )
);

// ----------------------------------------------------------------------

export default function Router() {
  return useRoutes([
    {
      element: <MainLayout />,
      children: [
        {
          path: '/',
          element: <Navigate to="/pedigree" replace />,
        },
        {
          path: '/pedigree/*',
          element: <PedigreeLayout />,
          children: [
            {
              path: '',
              element: <PedigreeSearchPanel />,
            },
            {
              path: ':pedigreeId',
              element: <PedigreeSearchPanel />,
            },
          ],
        },
        {
          path: '/member',
          children: [
            {
              element: <MemberSearchPage />,
              index: true,
            },
            {
              path: 'profile',
              element: <MemberProfilePage />,
            },
            {
              path: 'admin',
              element: <AdminMemberManagementPage />,
            },
          ],
        },
        {
          path: '/herd',
          children: [
            {
              element: <OwnedHerdListPage />,
              index: true,
            },
            {
              path: 'audit',
              children: [
                {
                  element: <HerdAuditPage />,
                  index: true,
                },
                {
                  path: 'entry/:herdId/:year',
                  element: <HerdAuditEntry />,
                },
                {
                  path: 'admin',
                  children: [
                    {
                      element: <HerdAuditAdminYearListPage />,
                      index: true,
                    },
                    {
                      path: ':year',
                      element: <HerdAuditAdminHerdsPage />,
                    },
                    {
                      path: 'entry/:herdId/:year',
                      element: <HerdAuditAdminEntryPage />,
                    },
                  ],
                },
                { path: ':herdId', element: <HerdManagementPage /> },
                { path: ':herdId/:pedigreeId', element: <HerdManagementPage /> },
              ],
            },
          ],
        },
        {
          path: '/calf',
          children: [
            {
              element: <Navigate to="/pedigree" replace />,
              index: true,
            },
            {
              path: 'return',
              children: [
                { element: <CalfReturnListPanel />, index: true },
                {
                  path: 'admin',
                  children: [
                    { element: <CalfReturnAdminYearListPage />, index: true },
                    {
                      path: ':year',
                      element: <CalfReturnAdminHerdsPage />,
                    },
                    {
                      path: 'entry/:herdId/:year',
                      element: <CalfReturnAdminEntryFormPage />,
                    },
                  ],
                },
                {
                  path: 'entry/:herdId/:year',
                  element: <CalfReturnEntryFormPanel />,
                },
              ],
            },
          ],
        },
        {
          path: '/dam',
          children: [
            {
              element: <Navigate to="/pedigree" replace />,
              index: true,
            },
            {
              path: 'adl',
              children: [
                { element: <AdlHerdPage />, index: true },
                {
                  path: 'entry/:herdId/:year',
                  element: <AdlFormEntryPage />,
                },
                {
                  path: 'admin',
                  children: [
                    { element: <AdlAdminYearListPage />, index: true },
                    { path: ':herdId/:year', element: <AdlAdminFormEntryPage /> },
                    { path: ':year', element: <AdlAdminHerdsPanel /> },
                  ],
                },
              ],
            },
          ],
        },
        {
          path: '/transfers',
          children: [
            { element: <PedigreeTransfersPage />, index: true },
            {
              path: 'entry',
              element: <PedigreeTransferFormPage />,
              children: [
                {
                  path: ':transferId',
                  element: <PedigreeTransferFormPage />,
                },
              ],
            },
            { path: 'history', element: <PedigreeTransferHistoryPage /> },
            {
              path: 'admin',
              children: [
                { element: <AdminPedigreeTransfersPage />, index: true },
                { path: 'history', element: <AdminPedigreeTransferHistoryPage /> },
                {
                  path: 'entry/:transferId',
                  element: <AdminPedigreeTransferFormPage />,
                },
              ],
            },
          ],
        },
        {
          path: '/registrations',
          children: [
            { element: <PedigreeRegistrationsPage />, index: true },
            {
              path: 'entry',
              element: <PedigreeRegistrationFormPage />,
              children: [
                {
                  path: ':registrationId',
                  element: <PedigreeRegistrationFormPage />,
                },
              ],
            },
            { path: 'history', element: <PedigreeRegistrationHistoryPage /> },
            {
              path: 'admin',
              children: [
                { element: <AdminPedigreeRegistrationsPage />, index: true },
                { path: 'history', element: <AdminPedigreeRegistrationHistoryPage /> },
                {
                  path: 'entry/:registrationId',
                  element: <AdminPedigreeRegistrationFormPage />,
                },
              ],
            },
          ],
        },
        {
          path: 'admin',
          children: [
            {
              path: 'role/entry',
              element: <AdminRoleFormPage />,
              children: [
                {
                  path: ':roleId',
                  element: <AdminRoleFormPage />,
                },
              ],
            },
            {
              path: 'role',
              element: <AdminRolesPage />,
            },
            {
              path: 'offices',
              element: <AdminOfficesPage />,
            },
          ],
        },
      ],
    },
    {
      path: '/404',
      element: <LogoOnlyLayout />,
      children: [{ path: '', element: <NotFound /> }],
    },
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: '404', element: <NotFound /> },
        { path: '*', element: <Navigate to="/404" replace /> },
      ],
    },
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
}
